import { useState, useEffect } from "react";
import { Copy, Share2, Download, Check, ExternalLink, Facebook, Instagram, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { API } from "../config/api";

export default function PromotionsPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = async () => {
    try {
      const response = await fetch(`${API}/promos`);
      if (response.ok) {
        setPromos(await response.json());
      }
    } catch (error) {
      console.error("Error fetching promos:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (promo) => {
    const text = `${promo.title}\n\n${promo.text}${promo.button_link ? `\n\n${promo.button_link}` : ''}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(promo.id);
      toast.success("Text copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Failed to copy text");
    }
  };

  const sharePromo = async (promo) => {
    const shareData = {
      title: promo.title,
      text: promo.text,
      url: promo.button_link || window.location.origin
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error("Sharing failed");
        }
      }
    } else {
      copyText(promo);
    }
  };

  const downloadImage = async (promo) => {
    try {
      const response = await fetch(promo.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${promo.title.replace(/\s+/g, '-').toLowerCase()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Image downloaded!");
    } catch {
      // Fallback: open image in new tab
      window.open(promo.image_url, '_blank');
      toast.info("Image opened in new tab");
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <Facebook className="w-4 h-4" />;
      case 'instagram': return <Instagram className="w-4 h-4" />;
      case 'whatsapp': return <MessageCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="pt-20 bg-kerala-cream min-h-screen" data-testid="promotions-page">
      {/* Hero */}
      <section className="py-16 bg-kerala-green">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">Share the Word</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">
            Spread the Joy
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Help us share our events with friends and family on social media. 
            Copy the text, download images, and share with your network!
          </p>
        </div>
      </section>

      {/* Promos Grid */}
      <section className="section-padding">
        <div className="section-container">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
            </div>
          ) : promos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No promotional content available at the moment.</p>
              <p className="text-gray-500 mt-2">Check back soon for shareable content!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {promos.map((promo) => (
                <div key={promo.id} className="bg-white rounded-sm shadow-lg overflow-hidden" data-testid={`promo-card-${promo.id}`}>
                  {/* Image */}
                  <div className="aspect-square relative">
                    <img
                      src={promo.image_url}
                      alt={promo.title}
                      className="w-full h-full object-cover"
                    />
                    {promo.platforms && promo.platforms.length > 0 && (
                      <div className="absolute top-3 right-3 flex gap-1">
                        {promo.platforms.map((platform) => (
                          <span 
                            key={platform}
                            className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-kerala-green"
                          >
                            {getPlatformIcon(platform)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-xl text-kerala-green mb-3">
                      {promo.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-4">
                      {promo.text}
                    </p>

                    {promo.button_link && (
                      <a 
                        href={promo.button_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-kerala-terracotta hover:text-kerala-green text-sm mb-4"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {promo.button_text || "Learn More"}
                      </a>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-kerala-gold/10">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyText(promo)}
                        className="flex-1"
                        data-testid={`copy-btn-${promo.id}`}
                      >
                        {copiedId === promo.id ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sharePromo(promo)}
                        className="flex-1"
                        data-testid={`share-btn-${promo.id}`}
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        Share
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadImage(promo)}
                        className="flex-1"
                        data-testid={`download-btn-${promo.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Image
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding bg-kerala-cream-alt">
        <div className="section-container max-w-3xl text-center">
          <h2 className="font-heading text-2xl text-kerala-green mb-6">
            How to Share
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4">
              <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Copy className="w-6 h-6 text-kerala-green" />
              </div>
              <h3 className="font-heading text-lg text-kerala-green mb-2">1. Copy Text</h3>
              <p className="text-gray-600 text-sm">
                Click "Copy" to copy the promotional text to your clipboard
              </p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-6 h-6 text-kerala-green" />
              </div>
              <h3 className="font-heading text-lg text-kerala-green mb-2">2. Download Image</h3>
              <p className="text-gray-600 text-sm">
                Click "Image" to download the promotional graphic
              </p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-kerala-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-6 h-6 text-kerala-green" />
              </div>
              <h3 className="font-heading text-lg text-kerala-green mb-2">3. Post & Share</h3>
              <p className="text-gray-600 text-sm">
                Paste the text and upload the image to your social media
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
